import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import CONFIG from "./config";
import { fetchPairData, fetchTransactions, fetchTokenSupply, calculateBurnData, fetchBuybackTotalSol } from "./data/api";
import { HeroSection } from "./components/HeroSection";
import { StatsBar } from "./components/StatsBar";
import { LoreSection } from "./components/LoreSection";
import { BurnTracker } from "./components/BurnTracker";
import { LiveTxFeed } from "./components/LiveTxFeed";
import { ArtGallery } from "./components/ArtGallery";
import { Footer } from "./components/Footer";

function App() {
  const [stats, setStats] = useState(null);
  const [burnData, setBurnData] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [buybackTotalSol, setBuybackTotalSol] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [pairData, txData, burnFromChain, buybackSol] = await Promise.allSettled([
        fetchPairData(),
        fetchTransactions(),
        fetchTokenSupply(),
        fetchBuybackTotalSol(),
      ]);

      if (pairData.status === "fulfilled" && pairData.value) {
        setStats(pairData.value);
        setBurnData(
          burnFromChain.status === "fulfilled" && burnFromChain.value
            ? burnFromChain.value
            : calculateBurnData(pairData.value)
        );
        setPriceHistory((prev) => {
          const snap = {
            price_usd: pairData.value.price_usd,
            volume_24h: pairData.value.volume_24h,
            market_cap: pairData.value.market_cap,
            _ts: Date.now(),
          };
          const next = [...prev, snap];
          return next.slice(-200);
        });
      }

      if (txData.status === "fulfilled") {
        setTransactions(txData.value || []);
      }
      if (buybackSol.status === "fulfilled" && typeof buybackSol.value === "number") {
        setBuybackTotalSol(buybackSol.value);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, CONFIG.REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <div
        data-testid="loading-screen"
        className="min-h-screen flex flex-col items-center justify-center bg-[#050404]"
      >
        <div className="relative">
          <div className="w-12 h-12 border-2 border-fire-red/20 border-t-fire-red animate-spin" style={{ borderRadius: "50%" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-fire-red/30 animate-fire-pulse" style={{ borderRadius: "50%" }} />
          </div>
        </div>
        <p className="font-cinzel text-sm text-fire-red/60 uppercase tracking-[0.3em] mt-6">Forging...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050404] relative">
      <div className="grain-overlay" />
      <div className="vignette" />

      <div className="relative z-10">
        <HeroSection stats={stats} />
        <StatsBar stats={stats} />
        <LoreSection />

        <main className="px-4 sm:px-6 md:px-12 lg:px-20 mt-10 sm:mt-16">
          <div className="section-reveal">
            <BurnTracker stats={stats} priceHistory={priceHistory} burnData={burnData} buybackTotalSol={buybackTotalSol} />
          </div>

          <div className="mt-4 sm:mt-6 section-reveal">
            <LiveTxFeed transactions={transactions} />
          </div>
        </main>

        <ArtGallery />
        <Footer />
      </div>
    </div>
  );
}

export default App;
